import { 
  collection, 
  addDoc, 
  updateDoc,
  deleteDoc,
  doc, 
  getDocs,
  query,
  where,
  Timestamp,
  DocumentData,
  orderBy,
  serverTimestamp,
  onSnapshot,
  writeBatch,
  arrayUnion,
  arrayRemove,
  setDoc,
  getDoc
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from '@firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { db, auth, storage } from './firebase';
import { BakingSession, Starter } from '../types/baking';
import { Challenge } from '../types/challenges';
import { addNotification } from './notificationStore';
import { sendPushNotification } from './notifications';
import { logger } from './logger';

// Helper function to convert dates to Firestore Timestamps
function convertDatesToTimestamps(data: any): any {
  if (data instanceof Date) {
    return Timestamp.fromDate(data);
  }
  
  if (Array.isArray(data)) {
    return data.map(item => convertDatesToTimestamps(item));
  }
  
  if (data && typeof data === 'object') {
    const converted: { [key: string]: any } = {};
    for (const [key, value] of Object.entries(data)) {
      if (value !== undefined && value !== null) {
        converted[key] = convertDatesToTimestamps(value);
      }
    }
    return converted;
  }
  
  return data;
}

// Helper function to convert Firestore Timestamps to Dates
function convertTimestampsToDates(data: any): any {
  if (data instanceof Timestamp) {
    return data.toDate();
  }
  
  if (Array.isArray(data)) {
    return data.map(item => convertTimestampsToDates(item));
  }
  
  if (data && typeof data === 'object') {
    const converted: { [key: string]: any } = {};
    for (const [key, value] of Object.entries(data)) {
      converted[key] = convertTimestampsToDates(value);
    }
    return converted;
  }
  
  return data;
}

// Ensure user is authenticated
function getUserId(): string {
  const user = auth.currentUser;
  if (!user) throw new Error('User must be authenticated');
  return user.uid;
}

// Starters Collection Operations
export function subscribeToStarters(userId: string, callback: (starters: Starter[]) => void) {
  logger.info('subscribeToStarters called', {
    component: 'store',
    function: 'subscribeToStarters',
    userId,
    authCurrentUser: !!auth.currentUser,
    authCurrentUserId: auth.currentUser?.uid
  });

  // Guard: Ensure auth is ready and matches userId
  if (!auth.currentUser) {
    logger.warn('subscribeToStarters called before auth ready', {
      component: 'store',
      function: 'subscribeToStarters',
      userId
    });
    // Return empty unsubscribe function
    return () => {};
  }

  if (auth.currentUser.uid !== userId) {
    logger.error('subscribeToStarters userId mismatch', null, {
      component: 'store',
      function: 'subscribeToStarters',
      requestedUserId: userId,
      authUserId: auth.currentUser.uid
    });
    // Return empty unsubscribe function
    return () => {};
  }

  const startersRef = collection(db, 'users', userId, 'starters');

  logger.debug('Creating Firestore subscription for starters', {
    component: 'store',
    function: 'subscribeToStarters',
    userId
  });

  return onSnapshot(
    startersRef,
    (snapshot) => {
      logger.info('Starters snapshot received', {
        component: 'store',
        function: 'subscribeToStarters',
        userId,
        count: snapshot.docs.length
      });

      const starters = snapshot.docs.map(doc => ({
        ...convertTimestampsToDates(doc.data()),
        id: doc.id,
      } as Starter));
      callback(starters);
    },
    (error) => {
      logger.error('Starters subscription error', error, {
        component: 'store',
        function: 'subscribeToStarters',
        userId
      });
    }
  );
}

export async function addStarter(starterData: Omit<Starter, 'id' | 'createdAt'>): Promise<void> {
  try {
    // Guard against unauthenticated calls
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to add starters');
    }
    const userId = currentUser.uid;

    const startersRef = collection(db, 'users', userId, 'starters');

    await addDoc(startersRef, convertDatesToTimestamps({
      ...starterData,
      userId,
      createdAt: new Date()
    }));
  } catch (error) {
    console.error('Error adding starter:', error);
    throw error;
  }
}

export async function updateStarter(starterId: string, updates: Partial<Starter>): Promise<void> {
  try {
    // Guard against unauthenticated calls
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to update starters');
    }
    const userId = currentUser.uid;

    const starterRef = doc(db, 'users', userId, 'starters', starterId);
    await updateDoc(starterRef, convertDatesToTimestamps(updates));
  } catch (error) {
    console.error('Error updating starter:', error);
    throw error;
  }
}

export async function deleteStarter(starterId: string): Promise<void> {
  try {
    // Guard against unauthenticated calls
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to delete starters');
    }
    const userId = currentUser.uid;

    const batch = writeBatch(db);

    // Delete the starter document
    const starterRef = doc(db, 'users', userId, 'starters', starterId);
    batch.delete(starterRef);

    // Delete associated baking sessions
    const sessionsQuery = query(
      collection(db, 'users', userId, 'bakingSessions'),
      where('starterId', '==', starterId)
    );
    const sessionsSnapshot = await getDocs(sessionsQuery);
    sessionsSnapshot.forEach(doc => {
      batch.delete(doc.ref);
    });

    await batch.commit();
  } catch (error) {
    console.error('Error deleting starter:', error);
    throw error;
  }
}

// Baking Sessions Operations
export function subscribeToBakingSessions(userId: string, callback: (sessions: BakingSession[]) => void) {
  logger.info('subscribeToBakingSessions called', {
    component: 'store',
    function: 'subscribeToBakingSessions',
    userId,
    authCurrentUser: !!auth.currentUser,
    authCurrentUserId: auth.currentUser?.uid
  });

  // Guard: Ensure auth is ready and matches userId
  if (!auth.currentUser) {
    logger.warn('subscribeToBakingSessions called before auth ready', {
      component: 'store',
      function: 'subscribeToBakingSessions',
      userId
    });
    // Return empty unsubscribe function
    return () => {};
  }

  if (auth.currentUser.uid !== userId) {
    logger.error('subscribeToBakingSessions userId mismatch', null, {
      component: 'store',
      function: 'subscribeToBakingSessions',
      requestedUserId: userId,
      authUserId: auth.currentUser.uid
    });
    // Return empty unsubscribe function
    return () => {};
  }

  const sessionsRef = collection(db, 'users', userId, 'bakingSessions');
  const q = query(sessionsRef, where('status', '!=', 'cancelled'));

  logger.debug('Creating Firestore subscription for baking sessions', {
    component: 'store',
    function: 'subscribeToBakingSessions',
    userId
  });

  return onSnapshot(
    q,
    (snapshot) => {
      logger.info('Baking sessions snapshot received', {
        component: 'store',
        function: 'subscribeToBakingSessions',
        userId,
        count: snapshot.docs.length
      });

      const sessions = snapshot.docs
        .map(doc => ({
          ...convertTimestampsToDates(doc.data()),
          id: doc.id,
          userId, // Ensure userId is always present
        } as BakingSession))
        .sort((a, b) => {
          const dateA = new Date(a.targetBakeTime).getTime();
          const dateB = new Date(b.targetBakeTime).getTime();
          return dateA - dateB;
        });
      callback(sessions);
    },
    (error) => {
      logger.error('Baking sessions subscription error', error, {
        component: 'store',
        function: 'subscribeToBakingSessions',
        userId
      });
    }
  );
}

export async function addBakingSession(session: Partial<BakingSession>): Promise<string> {
  try {
    // Guard against unauthenticated calls
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to add baking sessions');
    }
    const userId = currentUser.uid;

    const sessionsRef = collection(db, 'users', userId, 'bakingSessions');

    const sessionData = convertDatesToTimestamps({
      userId,
      name: session.name || 'Untitled Session',
      status: 'scheduled',
      targetBakeTime: session.targetBakeTime,
      fermentationType: session.fermentationType,
      ovenType: session.ovenType || 'domestic',
      starterId: session.starterId,
      starterSchedule: session.starterSchedule,
      woodOvenDetails: session.woodOvenDetails,
      customBulkTime: session.customBulkTime,
      customProofTime: session.customProofTime
    });

    const docRef = await addDoc(sessionsRef, sessionData);
    return docRef.id;
  } catch (error) {
    console.error('Error adding baking session:', error);
    throw error;
  }
}

export async function updateBakingSession(id: string, updates: Partial<BakingSession>): Promise<void> {
  try {
    // Guard against unauthenticated calls
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to update baking sessions');
    }
    const userId = currentUser.uid;

    const sessionRef = doc(db, 'users', userId, 'bakingSessions', id);
    const updateData = convertDatesToTimestamps(updates);
    await updateDoc(sessionRef, updateData);
  } catch (error) {
    console.error('Error updating baking session:', error);
    throw error;
  }
}

export async function deleteBakingSession(sessionId: string): Promise<void> {
  try {
    // Guard against unauthenticated calls
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to delete baking sessions');
    }
    const userId = currentUser.uid;

    const batch = writeBatch(db);

    // Delete the session document
    const sessionRef = doc(db, 'users', userId, 'bakingSessions', sessionId);

    // Get the session data first to check if there are any images to delete
    const sessionSnap = await getDoc(sessionRef);
    if (sessionSnap.exists()) {
      const sessionData = sessionSnap.data();
      if (sessionData.result?.imageUrl) {
        // Delete associated images if they exist
        const imageRef = ref(storage, sessionData.result.imageUrl);
        try {
          await deleteObject(imageRef);
        } catch (error) {
          console.warn('Error deleting session image:', error);
          // Continue with session deletion even if image deletion fails
        }
      }
    }

    // Delete the session document
    batch.delete(sessionRef);

    // Commit the batch
    await batch.commit();
  } catch (error) {
    console.error('Error deleting baking session:', error);
    throw error;
  }
}

// Challenge Operations
export async function notifyChallenge(
  userId: string,
  type: 'challenge_new' | 'challenge_update',
  title: string,
  message: string,
  data: any
) {
  // Guard against missing auth or invalid userId
  if (!auth.currentUser || !userId) {
    console.warn('Skipping notification: auth not ready or invalid userId');
    return;
  }

  try {
    // Add notification to panel and trigger push notification
    // Use non-blocking approach - don't await, just catch errors
    Promise.all([
      addNotification(userId, {
        type,
        title,
        message,
        data: {
          ...data,
          route: '/challenges'
        },
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 days
      }),
      sendPushNotification({
        title,
        body: message,
        data: {
          type,
          ...data
        }
      })
    ]).catch(error => {
      console.error('Error sending challenge notifications:', error);
    });
  } catch (error) {
    console.error('Error processing challenge notifications:', error);
  }
}

export async function createChallenge(
  challenge: Omit<Challenge, 'id'>,
  imageFile: File
): Promise<string> {
  try {
    if (auth.currentUser?.email !== 'breadflowbybadabec@gmail.com') {
      throw new Error('Only admin users can create challenges');
    }

    // Upload image first
    const imageFileName = `${uuidv4()}.jpg`;
    const imagePath = `challenges/${imageFileName}`;
    const imageRef = ref(storage, imagePath);

    const uploadTask = uploadBytesResumable(imageRef, imageFile);
    await uploadTask;

    // Get download URL
    const imageUrl = await getDownloadURL(imageRef);

    const challengesRef = collection(db, 'challenges');
    
    const challengeData = convertDatesToTimestamps({
      ...challenge,
      imageUrl,
      createdAt: new Date(),
      participants: [],
      isActive: true
    });

    const docRef = await addDoc(challengesRef, challengeData);

    // Get all users to notify them about the new challenge
    // Run notifications in background without blocking
    getDocs(collection(db, 'users')).then(usersSnapshot => {
      const notificationPromises = usersSnapshot.docs.map(userDoc =>
        notifyChallenge(
          userDoc.id,
          'challenge_new',
          'New Baking Challenge!',
          `Join "${challenge.title}" - a ${challenge.difficulty} level challenge`,
          {
            challengeId: docRef.id,
            challengeTitle: challenge.title
          }
        )
      );

      Promise.all(notificationPromises).catch(error => {
        console.error('Error sending new challenge notifications:', error);
      });
    }).catch(error => {
      console.error('Error fetching users for notifications:', error);
    });

    return docRef.id;
  } catch (error) {
    console.error('Error creating challenge:', error);
    throw error;
  }
}

export function subscribeToChallenges(
  callback: (challenges: Challenge[]) => void,
  errorCallback?: (error: Error) => void
) {
  logger.info('subscribeToChallenges called', {
    component: 'store',
    function: 'subscribeToChallenges',
    authCurrentUser: !!auth.currentUser,
    authCurrentUserId: auth.currentUser?.uid
  });

  // Guard: Ensure auth is ready
  if (!auth.currentUser) {
    logger.warn('subscribeToChallenges called before auth ready', {
      component: 'store',
      function: 'subscribeToChallenges'
    });
    // Return empty unsubscribe function
    return () => {};
  }

  const challengesRef = collection(db, 'challenges');
  const user = auth.currentUser;

  // Simple query to get all challenges - we'll filter client-side
  // This avoids needing a composite index
  logger.debug('Creating Firestore subscription for challenges', {
    component: 'store',
    function: 'subscribeToChallenges',
    userId: user.uid
  });

  return onSnapshot(
    challengesRef,
    (snapshot) => {
      logger.info('Challenges snapshot received', {
        component: 'store',
        function: 'subscribeToChallenges',
        count: snapshot.docs.length
      });

      const allChallenges = snapshot.docs.map(doc => ({
        ...convertTimestampsToDates(doc.data()),
        id: doc.id,
      } as Challenge));

      // Filter and sort on client side
      const challenges = allChallenges
        .filter(challenge => {
          if (challenge.isActive) return true;

          // Keep completed challenges by the user
          const userParticipation = challenge.participants.find(p => p.userId === user?.uid);
          if (userParticipation?.completed) return true;

          // Keep challenges in grace period for participating users
          if (userParticipation && challenge.gracePeriodEnds && new Date(challenge.gracePeriodEnds) > new Date()) {
            return true;
          }

          return false;
        })
        .sort((a, b) => {
          const dateA = a.endDate ? new Date(a.endDate).getTime() : 0;
          const dateB = b.endDate ? new Date(b.endDate).getTime() : 0;
          return dateB - dateA;
        });

      logger.debug('Challenges filtered and sorted', {
        component: 'store',
        function: 'subscribeToChallenges',
        totalCount: allChallenges.length,
        filteredCount: challenges.length
      });

      callback(challenges);
    },
    (error) => {
      logger.error('Challenges subscription error', error, {
        component: 'store',
        function: 'subscribeToChallenges',
        userId: user?.uid
      });

      if (errorCallback) {
        errorCallback(error as Error);
      }
    }
  );
}

export async function joinChallenge(challengeId: string): Promise<void> {
  try {
    // Guard against unauthenticated calls
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to join challenges');
    }
    const userId = currentUser.uid;

    const batch = writeBatch(db);

    // Get challenge reference
    const challengeRef = doc(db, 'challenges', challengeId);
    const challengeSnap = await getDoc(challengeRef);

    if (!challengeSnap.exists()) {
      throw new Error('Challenge not found');
    }

    // Create participant data
    const participantData = {
      userId,
      joinedAt: new Date(),
      progress: 0,
      completed: false
    };

    // Update challenge participants
    batch.update(challengeRef, {
      participants: arrayUnion(participantData)
    });

    // Create user's challenge document
    const userChallengeRef = doc(db, 'users', userId, 'challenges', challengeId);
    const userChallengeData = {
      challengeId,
      userId,
      progress: 0,
      currentStep: 0,
      startedAt: new Date(),
      lastUpdated: new Date(),
      completed: false
    };

    batch.set(userChallengeRef, convertDatesToTimestamps(userChallengeData));

    // Commit both operations atomically
    await batch.commit();
  } catch (error) {
    console.error('Error joining challenge:', error);
    throw error;
  }
}

export async function quitChallenge(challengeId: string): Promise<void> {
  try {
    // Guard against unauthenticated calls
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to quit challenges');
    }
    const userId = currentUser.uid;

    const batch = writeBatch(db);

    // Get challenge reference and data
    const challengeRef = doc(db, 'challenges', challengeId);
    const challengeSnap = await getDoc(challengeRef);

    if (!challengeSnap.exists()) {
      throw new Error('Challenge not found');
    }

    const challengeData = challengeSnap.data();
    const participant = challengeData.participants.find((p: any) => p.userId === userId);

    if (participant) {
      // Remove user from challenge participants
      batch.update(challengeRef, {
        participants: arrayRemove(participant)
      });
    }

    // Delete user's challenge document
    const userChallengeRef = doc(db, 'users', userId, 'challenges', challengeId);
    batch.delete(userChallengeRef);

    // Commit both operations atomically
    await batch.commit();
  } catch (error) {
    console.error('Error quitting challenge:', error);
    throw error;
  }
}

export async function completeChallenge(
  challengeId: string,
  imageFile: File,
  notes: string
): Promise<void> {
  try {
    // Guard against unauthenticated calls
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to complete challenges');
    }
    const userId = currentUser.uid;

    const batch = writeBatch(db);

    // Upload image
    const imageFileName = `${uuidv4()}.jpg`;
    const imagePath = `users/${userId}/challenges/${challengeId}/${imageFileName}`;
    const imageRef = ref(storage, imagePath);

    const uploadTask = uploadBytesResumable(imageRef, imageFile);
    await uploadTask;

    // Get download URL
    const imageUrl = await getDownloadURL(imageRef);

    // Get challenge reference
    const challengeRef = doc(db, 'challenges', challengeId);
    const challengeSnap = await getDoc(challengeRef);

    if (!challengeSnap.exists()) {
      throw new Error('Challenge not found');
    }

    // Update participant data in challenge document
    const challenge = challengeSnap.data();
    const updatedParticipants = challenge.participants.map((p: any) => {
      if (p.userId === userId) {
        return {
          ...p,
          completed: true,
          progress: 100,
          completedAt: new Date(),
          result: {
            imageUrl,
            notes,
            sharedToFacebook: false,
            likes: 0,
            likedBy: []
          }
        };
      }
      return p;
    });

    batch.update(challengeRef, {
      participants: updatedParticipants
    });

    // Update user's challenge document
    const userChallengeRef = doc(db, 'users', userId, 'challenges', challengeId);
    batch.update(userChallengeRef, {
      completed: true,
      progress: 100,
      completedAt: new Date(),
      lastUpdated: new Date(),
      result: {
        imageUrl,
        notes,
        sharedToFacebook: false,
        likes: 0,
        likedBy: []
      }
    });

    // Commit both operations atomically
    await batch.commit();
  } catch (error) {
    console.error('Error completing challenge:', error);
    throw error;
  }
}

export async function closeChallenge(challengeId: string): Promise<void> {
  try {
    const user = auth.currentUser;
    if (!user || user.email !== 'breadflowbybadabec@gmail.com') {
      throw new Error('Only admin users can close challenges');
    }

    const challengeRef = doc(db, 'challenges', challengeId);
    const challengeSnap = await getDoc(challengeRef);

    if (!challengeSnap.exists()) {
      throw new Error('Challenge not found');
    }

    const challenge = challengeSnap.data();
    
    // Update challenge status
    await updateDoc(challengeRef, {
      isActive: false,
      closedAt: new Date(),
      gracePeriodEnds: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 days grace period
    });

    // Send notifications to participants who haven't completed
    // Run notifications in background without blocking
    const incompleteParticipants = challenge.participants.filter(p => !p.completed);

    const notificationPromises = incompleteParticipants.map(participant =>
      notifyChallenge(
        participant.userId,
        'challenge_update',
        'Challenge Closing Soon',
        `The challenge "${challenge.title}" is closing. You have 7 days to complete it!`,
        {
          challengeId,
          challengeTitle: challenge.title,
          gracePeriodEnds: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
        }
      )
    );

    Promise.all(notificationPromises).catch(error => {
      console.error('Error sending challenge closing notifications:', error);
    });
  } catch (error) {
    console.error('Error closing challenge:', error);
    throw error;
  }
}

export async function deleteChallenge(challengeId: string): Promise<void> {
  try {
    const user = auth.currentUser;
    if (!user || user.email !== 'breadflowbybadabec@gmail.com') {
      throw new Error('Only admin users can delete challenges');
    }

    const challengeRef = doc(db, 'challenges', challengeId);
    const challengeSnap = await getDoc(challengeRef);

    if (!challengeSnap.exists()) {
      throw new Error('Challenge not found');
    }

    const challenge = challengeSnap.data();
    const batch = writeBatch(db);

    // Delete the challenge document
    batch.delete(challengeRef);

    // Commit the batch
    await batch.commit();

    // Send notifications to all participants in background
    // Don't block on notifications
    const notificationPromises = challenge.participants.map(participant =>
      addNotification(participant.userId, {
        type: 'challenge_update',
        title: 'Challenge Deleted',
        message: 'The challenge has been deleted by the BreadFlow team. We are very sorry for the inconvenience this may cause to you but a new challenge will be uploaded soon!',
        data: {
          challengeId,
          challengeTitle: challenge.title
        },
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 days
      })
    );

    Promise.all(notificationPromises).catch(error => {
      console.error('Error sending challenge deletion notifications:', error);
    });
  } catch (error) {
    console.error('Error deleting challenge:', error);
    throw error;
  }
}

export async function likeResult(challengeId: string, participantId: string): Promise<void> {
  try {
    // Guard against unauthenticated calls
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to like results');
    }
    const userId = currentUser.uid;

    const challengeRef = doc(db, 'challenges', challengeId);
    const challengeSnap = await getDoc(challengeRef);

    if (!challengeSnap.exists()) {
      throw new Error('Challenge not found');
    }

    const challenge = challengeSnap.data();
    const updatedParticipants = challenge.participants.map((p: any) => {
      if (p.userId === participantId) {
        const currentLikes = p.result?.likes || 0;
        const likedBy = p.result?.likedBy || [];

        // Toggle like
        if (likedBy.includes(userId)) {
          return {
            ...p,
            result: {
              ...p.result,
              likes: currentLikes - 1,
              likedBy: likedBy.filter((id: string) => id !== userId)
            }
          };
        } else {
          return {
            ...p,
            result: {
              ...p.result,
              likes: currentLikes + 1,
              likedBy: [...likedBy, userId]
            }
          };
        }
      }
      return p;
    });

    await updateDoc(challengeRef, {
      participants: updatedParticipants
    });
  } catch (error) {
    console.error('Error liking result:', error);
    throw error;
  }
}

export async function reportContent(
  challengeId: string,
  participantId: string,
  reason: string,
  description: string
): Promise<void> {
  try {
    // Guard against unauthenticated calls
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated to report content');
    }
    const userId = user.uid;

    const reportsRef = collection(db, 'contentReports');

    await addDoc(reportsRef, convertDatesToTimestamps({
      reportedBy: {
        userId,
        userName: user.displayName || user.email || 'Anonymous'
      },
      contentType: 'challenge_result',
      contentId: {
        challengeId,
        participantId
      },
      reason,
      description,
      status: 'pending',
      createdAt: new Date()
    }));
  } catch (error) {
    console.error('Error reporting content:', error);
    throw error;
  }
}

export function subscribeToContentReports(callback: (reports: any[]) => void) {
  const reportsRef = collection(db, 'contentReports');
  const q = query(reportsRef, orderBy('createdAt', 'desc'));

  return onSnapshot(q, (snapshot) => {
    const reports = snapshot.docs.map(doc => ({
      ...convertTimestampsToDates(doc.data()),
      id: doc.id,
    }));
    callback(reports);
  });
}

export async function updateReportStatus(
  reportId: string,
  status: 'reviewed' | 'resolved' | 'dismissed',
  action?: 'hidden' | 'deleted' | 'no_action',
  adminNotes?: string
): Promise<void> {
  try {
    // Guard against unauthenticated calls
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User must be authenticated to update report status');
    }
    const userId = user.uid;

    const reportRef = doc(db, 'contentReports', reportId);

    await updateDoc(reportRef, convertDatesToTimestamps({
      status,
      reviewedAt: new Date(),
      reviewedBy: {
        userId,
        userName: user.displayName || user.email || 'Admin'
      },
      action,
      adminNotes
    }));
  } catch (error) {
    console.error('Error updating report status:', error);
    throw error;
  }
}

export async function hideContent(challengeId: string, participantId: string): Promise<void> {
  try {
    const challengeRef = doc(db, 'challenges', challengeId);
    const challengeSnap = await getDoc(challengeRef);

    if (!challengeSnap.exists()) {
      throw new Error('Challenge not found');
    }

    const challenge = challengeSnap.data();
    const updatedParticipants = challenge.participants.map((p: any) => {
      if (p.userId === participantId && p.result) {
        return {
          ...p,
          result: {
            ...p.result,
            hidden: true
          }
        };
      }
      return p;
    });

    await updateDoc(challengeRef, {
      participants: updatedParticipants
    });
  } catch (error) {
    console.error('Error hiding content:', error);
    throw error;
  }
}

export async function unhideContent(challengeId: string, participantId: string): Promise<void> {
  try {
    const challengeRef = doc(db, 'challenges', challengeId);
    const challengeSnap = await getDoc(challengeRef);

    if (!challengeSnap.exists()) {
      throw new Error('Challenge not found');
    }

    const challenge = challengeSnap.data();
    const updatedParticipants = challenge.participants.map((p: any) => {
      if (p.userId === participantId && p.result) {
        const { hidden, ...result } = p.result;
        return {
          ...p,
          result
        };
      }
      return p;
    });

    await updateDoc(challengeRef, {
      participants: updatedParticipants
    });
  } catch (error) {
    console.error('Error unhiding content:', error);
    throw error;
  }
}

export async function deleteContentResult(challengeId: string, participantId: string): Promise<void> {
  try {
    const challengeRef = doc(db, 'challenges', challengeId);
    const challengeSnap = await getDoc(challengeRef);

    if (!challengeSnap.exists()) {
      throw new Error('Challenge not found');
    }

    const challenge = challengeSnap.data();
    const participant = challenge.participants.find((p: any) => p.userId === participantId);

    if (participant?.result?.imageUrl) {
      const imageRef = ref(storage, participant.result.imageUrl);
      try {
        await deleteObject(imageRef);
      } catch (error) {
        console.error('Error deleting image from storage:', error);
      }
    }

    const updatedParticipants = challenge.participants.map((p: any) => {
      if (p.userId === participantId) {
        const { result, ...rest } = p;
        return {
          ...rest,
          completed: false,
          completedAt: null
        };
      }
      return p;
    });

    await updateDoc(challengeRef, {
      participants: updatedParticipants
    });
  } catch (error) {
    console.error('Error deleting content result:', error);
    throw error;
  }
}